<script>
  // Team-Umfrage im Framework Assistant anlegen (Rueckmeldung 04.10.): schrittweise wie im Panel von Configure
  // Framework (TeamSurveyPanel) - erst Teams mit ihren Personen, dann Zeitplan, dann Einladung; je Team eine Umfrage
  import { createEventDispatcher, onMount } from "svelte";
  import Button from "lib/components/Button.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  export let projectId = "";
  export let teams = []; // Teams ohne Umfrage: { id, name, members } vorhandenes | { name } neues
  export let preselected = []; // im Team-Schritt gewaehlte Teams
  export let department = ""; // Department des Assistant - liefert die vorhandenen L1 fuer "Area"

  // Wie Team Settings: unter 3 Personen liessen sich Antworten einzelnen zuordnen
  const MIN_PEOPLE = 3;
  // "Area": optional Prozesse (L1), nach denen die Umfrage nacheinander fragt - gilt fuer alle Teams dieses Setups.
  const STEPS = ["Teams", "Area", "Schedule", "Invitation"];
  // wie LANGUAGES in backend/dam/team_survey.py; die Mails haben ausser bei Englisch die englische Fassung darunter
  // Namen in der jeweiligen Sprache selbst
  const MAIL_LANGUAGES = [
    ["de", "Deutsch"],
    ["en", "English"],
    ["fr", "Français"],
    ["it", "Italiano"],
    ["es", "Español"],
  ];
  // "Anna Beispiel <anna@firma.de>; ben@firma.de" - Name (optional) vor der Adresse bis zum Trenner ; , oder Zeilenende
  const PERSON =
    /(?:"([^"]*)"|([^;,\n<>@"]*?))\s*<?([^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+)>?/g;
  const dispatch = createEventDispatcher();
  // Mindestens eine gueltige Adresse im Eingabefeld - schaltet den Add-Button frei
  const hasEmail = (text) => [...(text || "").matchAll(PERSON)].length > 0;

  const localDate = (time) => {
    const date = new Date(time);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };
  const addDays = (day, days) =>
    localDate(new Date(`${day}T12:00:00`).getTime() + days * 86400000);
  const endOfDay = (day) => new Date(`${day}T23:59:00`).toISOString();
  const formatDay = (day) =>
    day
      ? new Date(`${day}T12:00:00`).toLocaleDateString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
        })
      : "";
  const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
  const people = (count) => (count === 1 ? "1 person" : `${count} people`);
  const START_LABELS = {
    now: "Right away",
    later: "At a later time",
    off: "Not yet (no e-mails)",
  };
  const teamKey = (team) => team.id || `new:${team.name.trim().toLowerCase()}`;
  const peopleOf = (team) =>
    (team?.members || []).map((person) => ({
      email: person.email,
      name: person.name || "",
    }));

  let step = 0;
  let blocks = [];
  let schedule = {
    // Nur Aufgaben sammeln
    mode: "collect",
    until: addDays(localDate(Date.now()), 7),
    reminder: true,
    reminder_days: 1,
    reminder2: false,
    reminder2_days: 2,
    period: true,
    period_from: "",
    period_until: "",
  };
  let invitation = {
    language: "de",
    contact_person: "",
    contact_person_email: "",
    email_signature: "",
    start: "now",
    start_at: "",
  };
  let busy = false;
  let error = "";
  let created = [];
  let copied = "";

  let askProcesses = false;
  let processes = []; // gewaehlt, in Abfrage-Reihenfolge: { id, name }
  let departmentProcesses = [];
  let departmentId = null;
  let newProcess = "";
  let processBusy = false;
  let processError = "";
  const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

  onMount(loadDepartmentProcesses);

  async function loadDepartmentProcesses() {
    if (!department) return;
    try {
      const response = await fetchWithSession("/api/damprojects/departments");
      const data = await response.json();
      const match = Array.isArray(data)
        ? data.find((item) => sameName(item.name || "", department))
        : null;
      departmentId = match?.id || null;
      // Teilprozesse (L2) je Prozess: waehlbar als Bereich "L1: L2" (Rueckmeldung 07.10.)
      const subResponse = await fetchWithSession(
        "/api/damprojects/subprocesses",
      );
      const subData = subResponse.ok ? await subResponse.json() : [];
      const subprocesses = Array.isArray(subData)
        ? subData
        : subData?.data || [];
      departmentProcesses = (match?.processes || [])
        .map(({ id, name }) => ({
          id,
          name,
          subprocesses: subprocesses
            .filter((sub) => sub.process_id === id)
            .map((sub) => ({ id: sub.id, name: sub.name, parent: name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      // Ohne Liste lassen sich Prozesse weiterhin neu eintragen
    }
  }

  function toggleProcess(process) {
    processes = processes.some((item) => item.id === process.id)
      ? processes.filter((item) => item.id !== process.id)
      : [...processes, process];
  }

  // Abgefragt wird je L2 (Rueckmeldung 07.10.): der Haken am L1 waehlt seine L2; nur ein L1 ohne L2 ist selbst ein Bereich
  const isSelected = (item) =>
    processes.some((selected) => selected.id === item.id);
  const wholeSelected = (process) =>
    process.subprocesses?.length
      ? process.subprocesses.every(isSelected)
      : isSelected(process);

  function toggleWholeProcess(process) {
    const subprocesses = process.subprocesses || [];
    if (!subprocesses.length)
      return toggleProcess({ id: process.id, name: process.name });
    processes = wholeSelected(process)
      ? processes.filter(
          (item) => !subprocesses.some((sub) => sub.id === item.id),
        )
      : [
          ...processes.filter((item) => item.id !== process.id),
          ...subprocesses.filter((sub) => !isSelected(sub)),
        ];
  }

  // Beim Anlegen sicherheitshalber: ein gewaehltes L1, das L2 hat, wird zu diesen L2
  $: areaSelection = processes
    .flatMap((item) =>
      departmentProcesses.find((process) => process.id === item.id)
        ?.subprocesses?.length
        ? departmentProcesses.find((process) => process.id === item.id)
            .subprocesses
        : [item],
    )
    .filter(
      (item, index, list) =>
        list.findIndex((other) => other.id === item.id) === index,
    );

  // Neuer L1 wird sofort im Framework angelegt; gibt es den Namen schon (Namen sind global eindeutig), wird der genommen
  async function addProcess() {
    const name = newProcess.trim();
    if (!name || processBusy) return;
    processBusy = true;
    processError = "";
    try {
      let process = departmentProcesses.find((item) =>
        sameName(item.name, name),
      );
      if (!process) {
        const listResponse = await fetchWithSession(
          "/api/damprojects/processes",
        );
        if (!listResponse.ok)
          throw new Error("Could not load processes. Please try again.");
        const existing = (await listResponse.json()).find((item) =>
          sameName(item.name || "", name),
        );
        if (existing) {
          process = { id: existing.id, name: existing.name };
        } else {
          const response = await fetchWithSession(
            "/api/damprojects/processes",
            {
              method: "POST",
              body: JSON.stringify({
                name,
                departments: departmentId ? [departmentId] : [],
              }),
            },
          );
          const result = await response.json().catch(() => ({}));
          const created = result.data || result;
          if (!response.ok || result.ok === false || !created?.id)
            throw new Error(
              result.message ||
                result.error ||
                `Could not create process ${name}.`,
            );
          process = { id: created.id, name: created.name || name };
        }
        departmentProcesses = [
          ...departmentProcesses,
          { ...process, subprocesses: [] },
        ].sort((a, b) => a.name.localeCompare(b.name));
        dispatch("frameworkChanged"); // Seitenleiste des Assistenten zeigt den neuen L1 sofort
      }
      if (!processes.some((item) => item.id === process.id))
        processes = [...processes, process];
      newProcess = "";
    } catch (addError) {
      processError = addError.message;
    } finally {
      processBusy = false;
    }
  }

  // Neuer Teilprozess (L2) unter einem Prozess, sofort im Framework angelegt und als Bereich "L1: L2" gewaehlt;
  // er ersetzt dann seinen Prozess als ganzen Bereich (Rueckmeldung 07.10.: L1 anlegen, dann x L2 davon)
  let newSubprocess = {}; // Eingabe je Prozess-Id
  let subprocessBusy = "";

  async function addSubprocess(process) {
    const name = (newSubprocess[process.id] || "").trim();
    if (!name || subprocessBusy) return;
    subprocessBusy = process.id;
    processError = "";
    try {
      let subprocess = (process.subprocesses || []).find((item) =>
        sameName(item.name, name),
      );
      if (!subprocess) {
        const response = await fetchWithSession(
          "/api/damprojects/subprocesses",
          {
            method: "POST",
            body: JSON.stringify({ name, process: process.id }),
          },
        );
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.ok === false)
          throw new Error(
            result.message ||
              result.error ||
              `Could not create subprocess ${name}.`,
          );
        let id = (result.data || result)?.id;
        if (!id) {
          // wie in Configure Framework: die Antwort traegt nicht immer die Id
          const listResponse = await fetchWithSession(
            "/api/damprojects/subprocesses",
          );
          const list = listResponse.ok ? await listResponse.json() : [];
          id = (Array.isArray(list) ? list : list?.data || []).find(
            (item) =>
              item.process_id === process.id && sameName(item.name || "", name),
          )?.id;
        }
        if (!id) throw new Error(`Could not create subprocess ${name}.`);
        subprocess = { id, name, parent: process.name };
        departmentProcesses = departmentProcesses.map((item) =>
          item.id === process.id
            ? {
                ...item,
                subprocesses: [...(item.subprocesses || []), subprocess],
              }
            : item,
        );
        dispatch("frameworkChanged");
      }
      processes = [
        ...processes.filter(
          (item) => item.id !== process.id && item.id !== subprocess.id,
        ),
        subprocess,
      ];
      newSubprocess = { ...newSubprocess, [process.id]: "" };
    } catch (addError) {
      processError = addError.message;
    } finally {
      subprocessBusy = "";
    }
  }

  // Erhebungsphase: 1 bis 4 Wochen nach Umfrage 1 - folgt der Frist, bis man sie selbst aendert
  let periodTouched = false;
  $: if (!periodTouched && schedule.until) {
    schedule.period_from = addDays(schedule.until, 7);
    schedule.period_until = addDays(schedule.until, 28);
  }

  // key: vorhandenes Team (teamKey), "new" fuer ein neues mit Namen, "" noch nicht gewaehlt
  function newBlock(team = null) {
    return {
      key: team ? teamKey(team) : teams.length ? "" : "new",
      name: "",
      people: peopleOf(team),
      draft: "",
    };
  }

  // Vorbelegen mit den im Team-Schritt gewaehlten Teams, sonst dem ersten freien - erneut, wenn die Teams erst nach
  // dem Oeffnen geladen sind, aber nur solange niemand etwas geaendert hat
  let prefill = "";
  $: if (
    !created.length &&
    (!blocks.length || JSON.stringify(blocks) === prefill)
  ) {
    const start = teams.filter((team) =>
      preselected.some((item) => teamKey(item) === teamKey(team)),
    );
    const next = (start.length ? start : teams.slice(0, 1)).map(newBlock);
    blocks = next.length ? next : [newBlock()];
    prefill = JSON.stringify(blocks);
  }

  // Beim Continue angelegte Projektteams, bis die Seite die Teams neu geladen hat
  let createdTeams = [];
  $: knownTeams = [
    ...teams,
    ...createdTeams.filter(
      (created) => !teams.some((team) => team.id === created.id),
    ),
  ];

  $: usedKeys = blocks
    .map((block) => block.key)
    .filter((key) => key && key !== "new");
  $: freeTeams = knownTeams.filter((team) => !usedKeys.includes(teamKey(team)));
  $: problems = blocks.map((block) => blockProblem(block));
  $: teamsReady = blocks.length > 0 && problems.every((problem) => !problem);
  $: invited = blocks.reduce((sum, block) => sum + block.people.length, 0);

  // privacy: unter MIN_PEOPLE Personen - wird hervorgehoben; alles andere ist ein ruhiger Hinweis
  function blockProblem(block) {
    if (!block.key) return { text: "Choose a team." };
    if (block.key === "new" && !block.name.trim())
      return { text: "Enter the name of the new team." };
    if (
      block.key !== "new" &&
      !knownTeams.some((team) => teamKey(team) === block.key)
    )
      return { text: "Choose a team." };
    if (block.invalidEmail)
      return {
        error: true,
        text: "Please enter a valid e-mail address, e.g. name@company.com.",
      };
    if (block.people.length && block.people.length < MIN_PEOPLE) {
      return {
        privacy: true,
        text: `At least ${MIN_PEOPLE} people per team. With fewer, answers could be traced back to individuals.`,
      };
    }
    return null;
  }

  function pickTeam(index, key) {
    const team = knownTeams.find((item) => teamKey(item) === key);
    blocks[index] = {
      ...blocks[index],
      key,
      name: "",
      people: team ? peopleOf(team) : blocks[index].people,
    };
  }

  // Eine oder mehrere Adressen auf einmal (auch eingefuegt aus Outlook: "Name <mail>; ...")
  function addPeople(index) {
    const block = blocks[index];
    const found = [...block.draft.matchAll(PERSON)];
    const people = [...block.people];
    for (const [, quoted, plain, email] of found) {
      if (
        !people.some(
          (person) => person.email.toLowerCase() === email.toLowerCase(),
        )
      ) {
        people.push({ email, name: (quoted ?? plain ?? "").trim() });
      }
    }
    // Text ohne gueltige Adresse bleibt stehen und wird als Fehler markiert
    blocks[index] = {
      ...block,
      people,
      draft: found.length ? "" : block.draft,
      invalidEmail: Boolean(block.draft.trim()) && !found.length,
    };
  }

  function removePerson(index, email) {
    blocks[index] = {
      ...blocks[index],
      people: blocks[index].people.filter((person) => person.email !== email),
    };
  }

  function draftKey(event, index) {
    if (["Enter", ",", ";"].includes(event.key)) {
      event.preventDefault();
      addPeople(index);
    }
  }

  async function create() {
    busy = true;
    error = "";
    const until = schedule.until;
    const startAt =
      invitation.start === "now"
        ? "now"
        : invitation.start === "later" && invitation.start_at
          ? new Date(invitation.start_at).toISOString()
          : null;
    try {
      // je Team eine Umfrage; angelegte verschwinden aus der Liste, damit ein erneuter Versuch sie nicht doppelt anlegt
      while (blocks.length) {
        const block = blocks[0];
        let team = knownTeams.find((item) => teamKey(item) === block.key);
        // Neues Team zuerst als Projektteam anlegen (wie addTeam im FrameworkAssistantChat); bei gleichem Namen das
        // vorhandene nehmen - so legt ein erneuter Versuch nach einem Fehler kein zweites an
        if (!team && projectId)
          team = await ensureProjectTeam(block.name.trim());
        const response = await fetchWithSession(
          "/api/damprojects/team-surveys",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              idproject: projectId,
              mode: schedule.mode,
              team_id: team?.id || "",
              team_name: team ? team.name : block.name.trim(),
              participants: block.people,
              start_at: startAt,
              collect_until: endOfDay(until),
              select_until: endOfDay(
                addDays(until, schedule.mode === "collect" ? 2 : 3),
              ),
              reminder_days: schedule.reminder ? schedule.reminder_days : null,
              reminder2_days: schedule.reminder2
                ? schedule.reminder2_days
                : null,
              period_from:
                schedule.period && schedule.period_from
                  ? new Date(`${schedule.period_from}T00:00:00`).toISOString()
                  : null,
              period_until:
                schedule.period && schedule.period_until
                  ? endOfDay(schedule.period_until)
                  : null,
              language: invitation.language,
              contact_person: invitation.contact_person.trim(),
              contact_person_email: invitation.contact_person_email.trim(),
              email_signature: invitation.email_signature.trim(),
              processes: askProcesses ? areaSelection : [],
            }),
          },
        );
        const result = await response.json().catch(() => ({}));
        if (!response.ok)
          throw new Error(
            `${team ? team.name : block.name}: ${result.message || "The survey could not be created."}`,
          );
        created = [...created, result];
        blocks = blocks.slice(1);
        dispatch("created", result);
      }
    } catch (createError) {
      error = createError.message;
    } finally {
      busy = false;
    }
  }

  // Continue nach "Teams": neue Teams sofort als Projektteams anlegen und im Block auf das angelegte Team umstellen
  async function continueAfterTeams() {
    if (!projectId) {
      step = 1;
      return;
    }
    busy = true;
    error = "";
    try {
      // Je Team nur eine Umfrage: ein eingetippter Name kann ein vorhandenes Team mit Umfrage treffen - hier melden statt erst beim Anlegen
      const surveyedTeamIds = await loadSurveyedTeamIds();
      for (let index = 0; index < blocks.length; index += 1) {
        const block = blocks[index];
        if (block.key !== "new") continue;
        const existing = await findProjectTeam(block.name.trim());
        if (existing && surveyedTeamIds.includes(existing.id)) throw new Error(`${existing.name} already has a survey. Please choose another team name.`);
        const team = existing || await ensureProjectTeam(block.name.trim());
        if (!knownTeams.some((item) => item.id === team.id)) {
          createdTeams = [
            ...createdTeams,
            { ...team, members: team.members || team.damUsers || [] },
          ];
        }
        blocks[index] = { ...block, key: teamKey(team), name: "" };
      }
      dispatch("teamsCreated");
      step = 1;
    } catch (teamError) {
      error = teamError.message;
    } finally {
      busy = false;
    }
  }

  async function loadSurveyedTeamIds() {
    const response = await fetchWithSession(`/api/damprojects/team-surveys?idproject=${encodeURIComponent(projectId)}`);
    if (!response.ok) return []; // ohne Liste meldet das Anlegen den Konflikt wie bisher
    return ((await response.json()).surveys || []).map((survey) => survey.team_id);
  }

  async function findProjectTeam(name) {
    const normalize = (value) => value.trim().toLowerCase();
    const listResponse = await fetchWithSession(
      `/api/damprojects/dam-teams?idproject=${encodeURIComponent(projectId)}`,
    );
    if (!listResponse.ok)
      throw new Error("Could not load project teams. Please try again.");
    return (
      (await listResponse.json()).find(
        (item) => normalize(item.name) === normalize(name),
      ) || null
    );
  }

  async function ensureProjectTeam(name) {
    const existing = await findProjectTeam(name);
    if (existing) return existing;
    const response = await fetchWithSession("/api/damprojects/dam-teams", {
      method: "POST",
      body: JSON.stringify({
        name,
        idproject: projectId,
        damUsers: [],
        subprocesses: [],
      }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok || !result.data?.id) {
      throw new Error(
        result.message || result.error || `Could not save team ${name}.`,
      );
    }
    return result.data;
  }

  async function copyLink(survey) {
    await navigator.clipboard?.writeText(
      `${window.location.origin}/app/team-survey?id=${survey.id}`,
    );
    copied = survey.id;
  }
</script>

{#if created.length && !blocks.length}
  <div class="setup-done">
    <strong class="done-title"
      >{created.length === 1
        ? "The survey is set up"
        : `${created.length} surveys are set up`}</strong
    >
    {#each created as survey}
      <div class="done-row">
        <div>
          <strong>{survey.team}</strong>
          <span
            >{!survey.expected_participants
              ? "Share the link with the team. It works without signing in."
              : !survey.start_at
                ? `${people(survey.expected_participants)}. Invitations not sent yet. Send them from Manage surveys.`
                : new Date(survey.start_at) > new Date()
                  ? `${people(survey.expected_participants)}. Invitations go out ${new Date(survey.start_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
                  : `${people(survey.expected_participants)} invited`}</span
          >
        </div>
        <button type="button" class="quiet" on:click={() => copyLink(survey)}
          >{copied === survey.id ? "Link copied" : "Copy link"}</button
        >
      </div>
    {/each}
    <button type="button" class="quiet" on:click={() => dispatch("manage")}
      >Manage surveys</button
    >
  </div>
{:else}
  <div class="setup">
    <ol class="setup-steps">
      {#each STEPS as label, index}
        <li class:current={index === step} class:complete={index < step}>
          <button
            type="button"
            disabled={index > step || busy}
            on:click={() => (step = index)}
            ><span>{index < step ? "✓" : index + 1}</span>{label}</button
          >
        </li>
      {/each}
    </ol>

    {#if step === 0}
      <p class="lead">
        Which teams take part, and who is in them? Everyone gets a personal
        link. The answers stay anonymous.
      </p>
      {#each blocks as block, index}
        <div class="team-block">
          <div class="row-label">
            <span>Team</span>
            {#if blocks.length > 1}
              <button
                type="button"
                class="quiet"
                on:click={() =>
                  (blocks = blocks.filter((_, item) => item !== index))}
                >Remove</button
              >
            {/if}
          </div>
          <div class="team-head">
            {#if knownTeams.length}
              <select
                value={block.key}
                on:change={(event) => pickTeam(index, event.target.value)}
              >
                <option value="" disabled>Select a team</option>
                {#each knownTeams.filter((team) => teamKey(team) === block.key || !usedKeys.includes(teamKey(team))) as team}
                  <option value={teamKey(team)}>{team.name}</option>
                {/each}
                <option value="new">New team …</option>
              </select>
            {/if}
            {#if block.key === "new"}
              <input
                bind:value={block.name}
                maxlength="120"
                placeholder="Name of the team, e.g. Accounts Payable"
              />
            {/if}
          </div>
          <div class="row-label">
            <span
              >People <em
                >e-mail addresses (paste several at once, e.g. from Outlook)</em
              ></span
            >
          </div>
          <div class="people">
            {#each block.people as person (person.email)}
              <span class="person" title={person.email}
                >{person.name || person.email}<button
                  type="button"
                  aria-label={`Remove ${person.email}`}
                  on:click={() => removePerson(index, person.email)}>×</button
                ></span
              >
            {/each}
            <input
              class="person-input"
              class:invalid={block.invalidEmail}
              bind:value={block.draft}
              placeholder={block.people.length
                ? "Add more"
                : "name@company.com"}
              aria-invalid={block.invalidEmail}
              on:input={() => (blocks[index].invalidEmail = false)}
              on:keydown={(event) => draftKey(event, index)}
              on:blur={() => addPeople(index)}
            />
            <Button
              size="sm"
              variant={hasEmail(block.draft) ? "secondary" : "paxAI"}
              text="Add"
              disabled={!hasEmail(block.draft)}
              on:click={() => addPeople(index)}
            />
          </div>
          {#if problems[index]?.error}
            <p class="error">{problems[index].text}</p>
          {:else if problems[index]?.privacy}
            <p class="warning">{problems[index].text}</p>
          {:else if problems[index]}
            <p class="hint">{problems[index].text}</p>
          {:else if !block.people.length}
            <p class="hint">
              No addresses: you share one link with the team yourself. Make sure
              at least {MIN_PEOPLE} people answer.
            </p>
          {:else}
            <p class="hint ok">✓ {people(block.people.length)}</p>
          {/if}
        </div>
      {/each}
      {#if error}<p class="warning">{error}</p>{/if}
      <div class="actions">
        <Button
          size="sm"
          variant="paxAI"
          text="+ Add another team"
          on:click={() =>
            (blocks = [
              ...blocks,
              freeTeams.length
                ? newBlock(freeTeams[0])
                : { ...newBlock(), key: "new" },
            ])}
        />
        <Button
          size="sm"
          variant="paxAI"
          text={busy ? "Saving teams…" : "Continue"}
          disabled={!teamsReady || busy}
          on:click={continueAfterTeams}
        />
      </div>
    {:else if step === 1}
      <!-- Recording period (aus Schedule hierher verschoben) wird wie bisher mitgesendet -->
      <p class="lead">Which areas or processes should the survey cover?</p>
      <section>
        <h4>Processes <em>optional</em></h4>
        <label class="check"
          ><input type="checkbox" bind:checked={askProcesses} /> Ask for tasks process
          by process</label
        >
        {#if askProcesses}
          <p class="hint">
            Add a process (L1) and its subprocesses (L2), or pick existing ones.
            The team is asked about one area after another, then about other
            tasks. Applies to all teams of this survey.
          </p>
          {#if departmentProcesses.length}
            <div class="process-list">
              {#each departmentProcesses as process (process.id)}
                <label class="check"
                  ><input
                    type="checkbox"
                    checked={processes && wholeSelected(process)}
                    on:change={() => toggleWholeProcess(process)}
                  />
                  {process.name}</label
                >
                {#each process.subprocesses || [] as subprocess (subprocess.id)}
                  <label class="check subprocess"
                    ><input
                      type="checkbox"
                      checked={processes.some(
                        (item) => item.id === subprocess.id,
                      )}
                      on:change={() => toggleProcess(subprocess)}
                    />
                    {subprocess.name}</label
                  >
                {/each}
                <div class="team-head subprocess">
                  <input
                    bind:value={newSubprocess[process.id]}
                    maxlength="120"
                    placeholder={`New subprocess of ${process.name}`}
                    on:keydown={(event) =>
                      event.key === "Enter" &&
                      (event.preventDefault(), addSubprocess(process))}
                  />
                  <Button
                    size="sm"
                    variant={newSubprocess[process.id]?.trim()
                      ? "secondary"
                      : "paxAI"}
                    text={subprocessBusy === process.id
                      ? "Adding…"
                      : "Add subprocess"}
                    disabled={!newSubprocess[process.id]?.trim() ||
                      Boolean(subprocessBusy)}
                    on:click={() => addSubprocess(process)}
                  />
                </div>
              {/each}
            </div>
          {:else}
            <p class="hint">
              {department
                ? `No processes in ${department} yet.`
                : "No department selected."} Add the first one below.
            </p>
          {/if}
          <div class="team-head">
            <input
              bind:value={newProcess}
              maxlength="120"
              placeholder="New process, e.g. Accounts Payable"
              on:keydown={(event) =>
                event.key === "Enter" && (event.preventDefault(), addProcess())}
            />
            <Button
              size="sm"
              variant={newProcess.trim() ? "secondary" : "paxAI"}
              text={processBusy ? "Adding…" : "Add process"}
              disabled={!newProcess.trim() || processBusy}
              on:click={addProcess}
            />
          </div>
          {#if processError}<p class="error">{processError}</p>{/if}
          {#if processes.length}
            <p class="hint ok">
              Order: {areaSelection
                .map(
                  (process, index) =>
                    `${index + 1}. ${process.parent ? `${process.parent}: ` : ""}${process.name}`,
                )
                .join(" · ")} · then other tasks
            </p>
          {/if}
        {/if}
      </section>
      <section>
        <h4>Recording period <em>optional</em></h4>
        <label class="check"
          ><input type="checkbox" bind:checked={schedule.period} /> Ask for each
          task whether it comes up in this period</label
        >
        {#if schedule.period}
          <p class="hint">
            So you know which tasks can actually be recorded. Suggested: one to
            four weeks after the survey.
          </p>
          <div class="two">
            <label
              ><span>From</span><input
                type="date"
                bind:value={schedule.period_from}
                on:input={() => (periodTouched = true)}
              /></label
            >
            <label
              ><span>Until</span><input
                type="date"
                bind:value={schedule.period_until}
                min={schedule.period_from || undefined}
                on:input={() => (periodTouched = true)}
              /></label
            >
          </div>
        {/if}
      </section>
      <div class="actions">
        <div class="previous-step">
          <Button
            size="sm"
            variant="paxAI"
            text="Previous step"
            iconLeft
            icon="arrow_left"
            on:click={() => (step = 0)}
          />
        </div>
        <Button
          size="sm"
          variant="paxAI"
          text="Continue"
          disabled={(askProcesses && !processes.length) ||
            (schedule.period &&
              (!schedule.period_from || !schedule.period_until))}
          on:click={() => (step = 2)}
        />
      </div>
    {:else if step === 2}
      <p class="lead">When does the team answer?</p>
      <section class="two">
        <label
          ><span>Survey open until</span><input
            type="date"
            bind:value={schedule.until}
            min={localDate(Date.now())}
          /></label
        >
      </section>
      <section>
        <h4>Reminders <em>only to people who have not answered yet</em></h4>
        <label class="check"
          ><input type="checkbox" bind:checked={schedule.reminder} /> First
          reminder
          <input
            type="number"
            min="1"
            max="30"
            bind:value={schedule.reminder_days}
            disabled={!schedule.reminder}
          />
          {schedule.reminder_days === 1 ? "day" : "days"} before the deadline</label
        >
        <label class="check"
          ><input type="checkbox" bind:checked={schedule.reminder2} /> Second
          reminder
          <input
            type="number"
            min="1"
            max="30"
            bind:value={schedule.reminder2_days}
            disabled={!schedule.reminder2}
          />
          {schedule.reminder2_days === 1 ? "day" : "days"} before the deadline</label
        >
      </section>
      <div class="actions">
        <div class="previous-step">
          <Button
            size="sm"
            variant="paxAI"
            text="Previous step"
            iconLeft
            icon="arrow_left"
            on:click={() => (step = 1)}
          />
        </div>
        <Button
          size="sm"
          variant="paxAI"
          text="Continue"
          disabled={!schedule.until}
          on:click={() => (step = 3)}
        />
      </div>
    {:else}
      <p class="lead">Who sends the invitation, and when?</p>
      <section class="two">
        <label
          ><span>Language of the e-mails</span>
          <select bind:value={invitation.language}
            >{#each MAIL_LANGUAGES as [code, label]}<option value={code}
                >{label}</option
              >{/each}</select
          ></label
        >
        <label
          ><span>Send invitations</span>
          <select bind:value={invitation.start}
            >{#each Object.entries(START_LABELS) as [value, label]}<option
                {value}>{label}</option
              >{/each}</select
          ></label
        >
        <p class="hint wide">
          {invitation.language === "en"
            ? "The e-mails are in English only."
            : "The e-mails include the English version below as a fallback."} The
          survey itself opens in each person's system language.
        </p>
        {#if invitation.start === "later"}
          <label class="wide"
            ><span>Invitations go out at</span><input
              type="datetime-local"
              bind:value={invitation.start_at}
            /></label
          >
        {/if}
      </section>
      <section class="two">
        <label
          ><span>Contact person <em>optional</em></span><input
            bind:value={invitation.contact_person}
            maxlength="300"
            placeholder="Name"
          /></label
        >
        <label
          ><span>Contact e-mail <em>optional</em></span><input
            type="email"
            bind:value={invitation.contact_person_email}
            maxlength="300"
            placeholder="name@company.com"
          /></label
        >
        <label class="wide"
          ><span>Signature <em>optional (closes every e-mail)</em></span>
          <textarea
            rows="2"
            bind:value={invitation.email_signature}
            maxlength="1000"
            placeholder={invitation.language === "de"
              ? "Euer Projektteam"
              : "Your project team"}
          ></textarea></label
        >
      </section>
      <dl class="summary">
        <dt>Teams</dt>
        <dd>
          {blocks
            .map(
              (block) =>
                (block.key === "new"
                  ? block.name
                  : knownTeams.find((team) => teamKey(team) === block.key)
                      ?.name) || "",
            )
            .join(", ")} · {invited ? people(invited) : "link only"}
        </dd>
        <dt>Area</dt>
        <dd>
          {askProcesses && areaSelection.length
            ? `${areaSelection.map((process) => (process.parent ? `${process.parent}: ${process.name}` : process.name)).join(", ")}, then other tasks`
            : "All tasks at once"}
        </dd>
        <dt>Survey</dt>
        <dd>until {formatDay(schedule.until)}</dd>
        <dt>Reminders</dt>
        <dd>
          {[
            schedule.reminder && `${plural(schedule.reminder_days, "day")}`,
            schedule.reminder2 && `${plural(schedule.reminder2_days, "day")}`,
          ]
            .filter(Boolean)
            .join(" and ") || "none"}{schedule.reminder || schedule.reminder2
            ? " before the deadline"
            : ""}
        </dd>
        <dt>Recording period</dt>
        <dd>
          {schedule.period
            ? `${formatDay(schedule.period_from)} to ${formatDay(schedule.period_until)}`
            : "not asked"}
        </dd>
        <dt>Invitations</dt>
        <dd>
          {START_LABELS[invitation.start]}{invitation.start === "later" &&
          invitation.start_at
            ? `, ${new Date(invitation.start_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
            : ""}
        </dd>
      </dl>
      {#if error}<p class="warning">{error}</p>{/if}
      <div class="actions">
        <div class="previous-step">
          <Button
            size="sm"
            variant="paxAI"
            text="Previous step"
            iconLeft
            icon="arrow_left"
            disabled={busy}
            on:click={() => (step = 2)}
          />
        </div>
        <Button
          size="sm"
          variant="paxAI"
          disabled={busy ||
            (invitation.start === "later" && !invitation.start_at)}
          on:click={create}
          text={busy
            ? "Creating…"
            : `Create ${blocks.length === 1 ? "survey" : `${blocks.length} surveys`}${invited && invitation.start === "now" ? " and send invitations" : ""}`}
        />
      </div>
    {/if}
  </div>
{/if}

<style>
  .setup,
  .setup-done {
    display: grid;
    gap: 1rem;
    width: 100%;
    box-sizing: border-box;
    padding: 1.25rem 1.4rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.9rem;
    background: var(--pax-surface-control-new);
    color: var(--pax-white-new);
    text-align: left;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .setup-steps {
    display: flex;
    gap: 1.5rem;
    margin: 0;
    padding: 0 0 0.85rem;
    border-bottom: 1px solid var(--pax-border-new);
    list-style: none;
  }

  .setup-steps button {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--pax-text-secondary-new);
    font: inherit;
    cursor: pointer;
  }

  .setup-steps button:disabled {
    cursor: default;
  }

  .setup-steps span {
    display: grid;
    place-items: center;
    width: 1.4rem;
    height: 1.4rem;
    border: 1px solid currentColor;
    border-radius: 50%;
    font-size: 0.75rem;
  }

  .setup-steps li.current button,
  .setup-steps li.complete button {
    color: var(--pax-white-new);
  }

  .setup-steps li.current button {
    font-weight: 600;
  }

  .setup-steps li.complete span {
    background: var(--pax-white-new);
    color: var(--pax-text-inverse-new);
  }

  p,
  dl,
  h4 {
    margin: 0;
  }

  .lead {
    font-size: 0.95rem;
  }

  section {
    display: grid;
    gap: 0.5rem;
  }

  h4,
  .row-label span,
  label > span {
    color: var(--pax-white-new);
    font-size: 0.85rem;
    font-weight: 600;
  }

  em {
    margin-left: 0.35rem;
    color: var(--pax-text-secondary-new);
    font-size: 0.8rem;
    font-style: normal;
    font-weight: 400;
  }

  .two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 1rem;
    align-items: end;
  }

  .wide {
    grid-column: 1 / -1;
  }

  label {
    display: grid;
    gap: 0.35rem;
  }

  .team-block {
    display: grid;
    gap: 0.45rem;
    padding: 0.9rem 1rem 1rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.7rem;
  }

  .row-label {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .row-label:not(:first-child) {
    margin-top: 0.35rem;
  }

  .team-head {
    display: flex;
    gap: var(--control-gap);
  }

  .team-head select,
  .team-head input {
    flex: 1;
  }

  input,
  select,
  textarea {
    box-sizing: border-box;
    width: 100%;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.5rem;
    background: transparent;
    color: var(--pax-white-new);
    font: inherit;
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--pax-text-secondary-new);
    opacity: 0.7;
  }

  select option {
    background: var(--pax-surface-control-new);
  }

  .people {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    align-items: center;
  }

  .person {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.25rem 0.35rem 0.25rem 0.7rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    font-size: 0.85rem;
  }

  .person button {
    padding: 0 0.25rem;
    border: 0;
    background: none;
    color: var(--pax-text-secondary-new);
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
  }

  .people .person-input {
    flex: 1 0 12rem;
    width: auto;
    border-style: dashed;
  }

  .people .person-input.invalid {
    border-color: var(--pax-danger);
  }

  .error {
    color: var(--pax-danger);
    font-size: 0.85rem;
  }

  .hint {
    color: var(--pax-text-secondary-new);
    font-size: 0.85rem;
  }

  .hint.ok {
    color: var(--pax-white-new);
  }

  .warning {
    color: var(--pax-orange);
    font-size: 0.85rem;
  }

  .process-list {
    display: grid;
    gap: 0.35rem;
    max-height: 22rem;
    overflow-y: auto;
  }

  .check.subprocess,
  .team-head.subprocess {
    padding-left: 1.6rem; /* L2 eingerueckt unter seinem L1 */
  }

  .team-head.subprocess {
    margin-bottom: 0.5rem;
  }

  .check {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .check input[type="checkbox"] {
    width: auto;
    margin: 0;
  }

  .check input[type="number"] {
    width: 4rem;
    padding: 0.3rem 0.5rem;
  }

  .summary {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.3rem 1rem;
    padding: 0.85rem 1rem;
    border-radius: 0.6rem;
    background: rgba(255, 255, 255, 0.05);
    font-size: 0.85rem;
  }

  .summary dt {
    color: var(--pax-text-secondary-new);
  }

  .summary dd {
    margin: 0;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--control-gap);
    padding-top: 0.25rem;
  }

  /* Zurueck links, Weiter rechts */
  .previous-step {
    margin-right: auto;
  }

  .done-title {
    font-size: 0.95rem;
  }

  .done-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.65rem 0;
    border-top: 1px solid var(--pax-border-new);
  }

  .done-row div {
    display: grid;
    gap: 0.15rem;
  }

  .done-row span {
    color: var(--pax-text-secondary-new);
  }

  .quiet {
    justify-self: start;
    padding: 0;
    border: 0;
    background: none;
    color: var(--pax-text-secondary-new);
    font: inherit;
    font-size: 0.85rem;
    text-decoration: underline;
    cursor: pointer;
  }

  @media (max-width: 700px) {
    .two {
      grid-template-columns: 1fr;
    }
  }
</style>
